/**
 * Client-side 15-puzzle (4×4 sliding tiles) solver.
 * Weighted A* with Manhattan distance + linear conflict.
 */

export const GRID_SIZE = 4;
export const EMPTY_CELL = null;
export const TILE_COUNT = GRID_SIZE * GRID_SIZE;

export function createSolvedPuzzle(gridSize = GRID_SIZE) {
    const n = gridSize * gridSize;
    const puzzle = [];
    for (let i = 1; i < n; i += 1) puzzle.push(i);
    puzzle.push(EMPTY_CELL);
    return puzzle;
}

export function isPuzzleSolved(state, gridSize = GRID_SIZE) {
    const n = gridSize * gridSize;
    for (let i = 0; i < n - 1; i += 1) {
        if (state[i] !== i + 1) return false;
    }
    return state[n - 1] === EMPTY_CELL;
}

export function adjacentPositions(position, gridSize = GRID_SIZE) {
    const row = Math.floor(position / gridSize);
    const col = position % gridSize;
    const next = [];
    if (row > 0) next.push(position - gridSize);
    if (row < gridSize - 1) next.push(position + gridSize);
    if (col > 0) next.push(position - 1);
    if (col < gridSize - 1) next.push(position + 1);
    return next;
}

export function countInversions(state) {
    const tiles = [];
    for (let i = 0; i < state.length; i += 1) {
        if (state[i] !== EMPTY_CELL) tiles.push(state[i]);
    }
    let inversions = 0;
    for (let i = 0; i < tiles.length; i += 1) {
        for (let j = i + 1; j < tiles.length; j += 1) {
            if (tiles[i] > tiles[j]) inversions += 1;
        }
    }
    return inversions;
}

/**
 * 15-puzzle solvability: even-width boards are solvable iff
 * (inversions + blank row from bottom) is odd.
 * Odd-width (8-puzzle): solvable iff inversions are even.
 */
export function isSolvable(state, gridSize = GRID_SIZE) {
    const inversions = countInversions(state);
    const emptyIdx = state.indexOf(EMPTY_CELL);
    if (emptyIdx < 0) return false;
    const blankRow = Math.floor(emptyIdx / gridSize);
    const blankRowFromBottom = gridSize - blankRow;
    if (gridSize % 2 === 1) return inversions % 2 === 0;
    return (inversions + blankRowFromBottom) % 2 === 1;
}

function mulberry32(seed) {
    let a = seed >>> 0;
    return () => {
        a += 0x6d2b79f5;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/**
 * Random-walk scramble from a board using only legal slides (always solvable
 * if the start state is solvable). `rng` may be a function or an integer seed.
 */
export function scramblePuzzle(state, slides = 40, rng = Math.random, gridSize = GRID_SIZE) {
    const rand = typeof rng === 'function' ? rng : mulberry32(rng);
    const board = state.slice();
    let empty = board.indexOf(EMPTY_CELL);
    let previous = -1;
    for (let i = 0; i < slides; i += 1) {
        const neighbors = adjacentPositions(empty, gridSize);
        const prev = previous;
        const choices = neighbors.filter((p) => p !== prev);
        const pool = choices.length ? choices : neighbors;
        const pos = pool[Math.floor(rand() * pool.length)];
        board[empty] = board[pos];
        board[pos] = EMPTY_CELL;
        previous = empty;
        empty = pos;
    }
    return board;
}

export function applyMoves(state, tiles, gridSize = GRID_SIZE) {
    const board = state.slice();
    for (let i = 0; i < tiles.length; i += 1) {
        const tile = tiles[i];
        const empty = board.indexOf(EMPTY_CELL);
        const pos = board.indexOf(tile);
        if (pos < 0 || !adjacentPositions(empty, gridSize).includes(pos)) {
            throw new Error(`illegal move: tile ${tile}`);
        }
        board[empty] = tile;
        board[pos] = EMPTY_CELL;
    }
    return board;
}

export function manhattanDistance(state, gridSize = GRID_SIZE) {
    let distance = 0;
    const n = gridSize * gridSize;
    for (let i = 0; i < n; i += 1) {
        const value = state[i];
        if (value === EMPTY_CELL) continue;
        const goal = value - 1;
        distance += Math.abs((i / gridSize | 0) - (goal / gridSize | 0))
            + Math.abs((i % gridSize) - (goal % gridSize));
    }
    return distance;
}

export function linearConflict(state, gridSize = GRID_SIZE) {
    let conflict = 0;
    for (let row = 0; row < gridSize; row += 1) {
        const vals = [];
        for (let col = 0; col < gridSize; col += 1) {
            const value = state[row * gridSize + col];
            if (value === EMPTY_CELL) continue;
            if (((value - 1) / gridSize | 0) === row) vals.push(value);
        }
        for (let i = 0; i < vals.length; i += 1) {
            for (let j = i + 1; j < vals.length; j += 1) {
                if (vals[i] > vals[j]) conflict += 2;
            }
        }
    }
    for (let col = 0; col < gridSize; col += 1) {
        const vals = [];
        for (let row = 0; row < gridSize; row += 1) {
            const value = state[row * gridSize + col];
            if (value === EMPTY_CELL) continue;
            if ((value - 1) % gridSize === col) vals.push(value);
        }
        for (let i = 0; i < vals.length; i += 1) {
            for (let j = i + 1; j < vals.length; j += 1) {
                if (vals[i] > vals[j]) conflict += 2;
            }
        }
    }
    return conflict;
}

function heuristic(state, gridSize = GRID_SIZE) {
    return manhattanDistance(state, gridSize) + linearConflict(state, gridSize);
}

function stateKey(state) {
    return state.join(',');
}

function reconstructPath(cameFrom, goalKey) {
    const tiles = [];
    let key = goalKey;
    while (cameFrom.has(key)) {
        const step = cameFrom.get(key);
        tiles.push(step.tile);
        key = step.prev;
    }
    tiles.reverse();
    return tiles;
}

function heapLess(a, b) {
    return a.f < b.f || (a.f === b.f && a.h < b.h);
}

class MinHeap {
    constructor() {
        this.data = [];
    }

    get length() {
        return this.data.length;
    }

    push(node) {
        const data = this.data;
        data.push(node);
        let i = data.length - 1;
        while (i > 0) {
            const p = (i - 1) >> 1;
            if (!heapLess(node, data[p])) break;
            data[i] = data[p];
            i = p;
        }
        data[i] = node;
    }

    pop() {
        const data = this.data;
        const top = data[0];
        const last = data.pop();
        if (!data.length) return top;
        let i = 0;
        const n = data.length;
        while (true) {
            const l = i * 2 + 1;
            const r = l + 1;
            if (l >= n) break;
            let smallest = l;
            if (r < n && heapLess(data[r], data[l])) smallest = r;
            if (!heapLess(data[smallest], last)) break;
            data[i] = data[smallest];
            i = smallest;
        }
        data[i] = last;
        return top;
    }
}

function searchOutcome(start, gridSize) {
    if (isPuzzleSolved(start, gridSize)) {
        return { path: [], solved: true };
    }
    if (!isSolvable(start, gridSize)) {
        return { path: null, unsolvable: true };
    }
    return null;
}

function createSearch(start, { weight, gridSize }) {
    const startKey = stateKey(start);
    const h0 = heuristic(start, gridSize);
    const open = new MinHeap();
    open.push({
        f: weight * h0,
        g: 0,
        h: h0,
        state: start.slice(),
        empty: start.indexOf(EMPTY_CELL),
        key: startKey,
    });
    return {
        open,
        visited: new Set([startKey]),
        cameFrom: new Map(),
        nodes: 0,
        weight,
        gridSize,
    };
}

function expandOnce(ctx) {
    if (!ctx.open.length) return { failed: true, path: null, nodes: ctx.nodes };
    const current = ctx.open.pop();
    ctx.nodes += 1;

    if (current.h === 0 && isPuzzleSolved(current.state, ctx.gridSize)) {
        return {
            path: reconstructPath(ctx.cameFrom, current.key),
            nodes: ctx.nodes,
        };
    }

    const neighbors = adjacentPositions(current.empty, ctx.gridSize);
    for (let i = 0; i < neighbors.length; i += 1) {
        const pos = neighbors[i];
        const tile = current.state[pos];
        const next = current.state.slice();
        next[current.empty] = tile;
        next[pos] = EMPTY_CELL;
        const key = stateKey(next);
        if (ctx.visited.has(key)) continue;
        ctx.visited.add(key);
        ctx.cameFrom.set(key, { prev: current.key, tile });
        const h = heuristic(next, ctx.gridSize);
        const g = current.g + 1;
        ctx.open.push({
            f: g + ctx.weight * h,
            g,
            h,
            state: next,
            empty: pos,
            key,
        });
    }
    return null;
}

/**
 * Synchronous weighted A*. Prefer this in tests.
 * Returns { path: number[] } tile sequence, or { unsolvable: true } / { failed: true }.
 */
export function solvePuzzle(start, options = {}) {
    const gridSize = options.gridSize || GRID_SIZE;
    const early = searchOutcome(start, gridSize);
    if (early) return early;

    const ctx = createSearch(start, {
        weight: options.weight ?? 1.6,
        gridSize,
    });
    const maxNodes = options.maxNodes ?? 300000;

    while (ctx.open.length && ctx.nodes < maxNodes) {
        const found = expandOnce(ctx);
        if (found) return found;
    }
    return { path: null, failed: true, nodes: ctx.nodes };
}

/**
 * Yielding solver so the UI can paint "Solving…" and honor Cancel.
 */
export async function solvePuzzleAsync(start, options = {}) {
    const gridSize = options.gridSize || GRID_SIZE;
    const early = searchOutcome(start, gridSize);
    if (early) return early;

    const ctx = createSearch(start, {
        weight: options.weight ?? 1.6,
        gridSize,
    });
    const maxNodes = options.maxNodes ?? 300000;
    const batchSize = options.batchSize ?? 2500;
    const { onProgress, isCancelled } = options;

    while (ctx.open.length && ctx.nodes < maxNodes) {
        const batchEnd = ctx.nodes + batchSize;
        while (ctx.open.length && ctx.nodes < batchEnd && ctx.nodes < maxNodes) {
            const found = expandOnce(ctx);
            if (found) return found;
        }
        if (typeof onProgress === 'function') {
            onProgress({ nodes: ctx.nodes, open: ctx.open.length });
        }
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (typeof isCancelled === 'function' && isCancelled()) {
            return { cancelled: true, path: null, nodes: ctx.nodes };
        }
    }
    return { path: null, failed: true, nodes: ctx.nodes };
}
